// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// Contrato do mapa — a forma compacta que atravessa a rede
// ============================================================================
//
// PORQUE ESTE FICHEIRO ESTÁ NUM PACOTE PARTILHADO
//   A forma compacta é um contrato entre quem a produz (API) e quem a consome
//   (mapa). Se cada lado declarasse o seu próprio tipo, inserir um campo no meio
//   do *tuple* no servidor não daria erro de compilação no cliente — este
//   passaria a ler índices errados **em silêncio**. Com uma só declaração, o
//   TypeScript recusa a compilação de imediato.
//
//   Por D-042 este formato é **definitivo**, o que torna a garantia mais
//   valiosa do que o custo do pacote extra.
//
// REGRA DESTE PACOTE
//   Nada de `node:*`, nada de DOM, nada de dependências. O `tsconfig` tem
//   `"types": []` justamente para tornar essa regra verificável pelo compilador
//   em vez de a deixar à disciplina de quem edita.
//
// PORQUE SE ENVIA O ANO INTEIRO DE UMA VEZ
//   MEDIDO com os 90 208 registos do gerador: **3,08 MB** de JSON, **0,48 MB**
//   comprimido com gzip. É o payload de um site com duas fotografias, e paga-se
//   uma vez ao abrir o plano. Enviar tudo e janelar no cliente é o que o *spike*
//   mediu (23,5–24,5 ms p95), e manter essa arquitectura é o que faz a medição
//   transferir-se para o produto.
//
//   ⚠️ Estes números são de UMA versão do contrato. Acrescentar um campo mede-se
//   outra vez: quando `estadoViatura` e `meio` entraram, o payload subiu de
//   2,56 para 3,08 MB. Um número medido que fica no comentário depois de o
//   contrato mudar deixa de ser medição e passa a ser folclore.
//
//   A alternativa — pedir cada janela ao servidor — acrescentaria latência de
//   rede a cada deslocamento, que o *spike* nunca mediu, e arriscaria o tecto de
//   500 ms (D-008) por razões alheias ao nosso código.
//
//   ⚠️ Isto vale para UM ano de UM plano. Com dez anos de histórico não serve —
//   nessa altura janela-se no servidor. Fica registado para não ser esquecido.
//
// FORMA COMPACTA
//   Índices inteiros em vez de strings repetidas: 250 × 365 células com nomes
//   repetidos passariam de 10 MB.
// ============================================================================
/**
 * Bandeiras por célula, em bits.
 *
 * Um inteiro em vez de oito booleanos: são 91 250 células, e cada campo a mais
 * multiplica-se por isso na serialização.
 *
 * ⚠️ Não há bandeira para «viatura indisponível»: essa informação é
 * `estadoViatura >= 0`. Uma bandeira a duplicar um campo que já existe é uma
 * bandeira que um dia deixa de concordar com ele.
 */
export const BANDEIRA = {
    MOVIMENTO_IN: 1,
    MOVIMENTO_OUT: 2,
    /**
     * Dia de rotação: chegada E partida no mesmo dia.
     *
     * É o objectivo O1 do projeto. Na folha actual são as células **pretas**, e os
     * planeadores já as pintam à mão — ou seja, já marcam manualmente os dias que
     * o algoritmo deve optimizar. Ver camada visual §4.
     *
     * ⚠️ Neste dia a **semântica das sublinhas inverte-se**: a sublinha 1 passa a
     * ser a CHEGADA do itinerário que termina e a sublinha 2 a PARTIDA do que
     * começa. É por isso que `movimentos` guarda o par separado, e não um texto
     * já junto: o mapa precisa dos dois lados em linhas diferentes.
     */
    ROTACAO: 4,
    RESERVA: 8,
    /** O «?» que os planeadores usam para registar intenção provisória (D-033). */
    DUVIDA: 16,
    PRIMEIRO_DIA: 32,
    ULTIMO_DIA: 64,
    /**
     * Dois condutores na MESMA viatura no MESMO dia, em simultâneo.
     *
     * Não é o mesmo que rotação sequencial (`ROTACAO`), e a diferença é legal, não
     * cosmética: a tripulação dupla cai no art. 4.º o) e no art. 8.º n.º 5 do
     * Reg. (CE) 561/2006 — 9 h de repouso em cada 30 h, em vez das 11 h em 24 h.
     * Medido: 95 células na folha real. Se o mapa as colapsasse numa só, o motor
     * de recomendação herdaria contadores errados.
     */
    TRIPULACAO_DUPLA: 128,
    /**
     * Dia marcado como rotação em que a rotação **não se sustenta**.
     *
     * O cliente apontou o caso: um dia negro seguido de dia livre não é rotação,
     * porque não há itinerário nenhum a partir. Medido nos dados sintéticos:
     * **915 de 6 405 dias de rotação (14,3 %) não têm partida no dia seguinte** —
     * uma em cada sete células negras estava errada.
     *
     * A rotação é agora **derivada** e não aceite do campo de origem. Onde a
     * marcação não se sustenta, `ROTACAO` é retirada e esta bandeira fica no seu
     * lugar: o mapa mostra o dia como chegada e assinala-o como suspeito. Corrigir
     * em silêncio seria pior — na folha real isto é provavelmente erro de registo,
     * e quem planeia tem de o ver.
     */
    ROTACAO_SUSPEITA: 256,
};
const ESTADOS = ['bom', 'aviso', 'grave', 'critico'];
function indexar(itens, chave) {
    const m = new Map();
    itens.forEach((t, i) => m.set(chave(t), i));
    return m;
}
/** Índice do dia no ano, 0-based. Devolve -1 se a data não é do ano dado. */
function diaDoAno(iso, ano) {
    const d = new Date(`${iso}T00:00:00Z`);
    if (Number.isNaN(d.getTime()) || d.getUTCFullYear() !== ano)
        return -1;
    return Math.round((d.getTime() - Date.UTC(ano, 0, 1)) / 86_400_000);
}
/**
 * Constrói a forma compacta a partir das linhas da origem.
 *
 * Pura de propósito: não lê disco nem rede, e por isso é testável com dados em
 * memória e reutilizável quando a origem passar a ser o PostgreSQL.
 */
export function construirDadosMapa(ano, csv) {
    const nDias = (Date.UTC(ano + 1, 0, 1) - Date.UTC(ano, 0, 1)) / 86_400_000;
    const fimDeSemana = [];
    const mesPorDia = [];
    const diaDoMes = [];
    for (let i = 0; i < nDias; i++) {
        const d = new Date(Date.UTC(ano, 0, 1 + i));
        const semana = d.getUTCDay(); // 0 = domingo, 6 = sábado
        fimDeSemana.push(semana === 0 || semana === 6);
        mesPorDia.push(d.getUTCMonth());
        diaDoMes.push(d.getUTCDate());
    }
    const iCliente = indexar(csv.clientes, (c) => c['id'] ?? '');
    const iMotorista = indexar(csv.motoristas, (m) => m['id'] ?? '');
    const iLocalidade = indexar(csv.localidades, (l) => l['codigo_interno'] ?? '');
    const iViatura = indexar(csv.viaturas, (v) => v['id'] ?? '');
    // Os itinerários não têm tabela própria no CSV do mapa: recolhem-se das
    // alocações, na ordem em que aparecem.
    const iItinerario = new Map();
    const itinerarios = [];
    // Tabelas de baixa cardinalidade, recolhidas na ordem em que aparecem. Não são
    // fixadas em código: a folha real tem `Oficina`, `Avariado` e `Sinistro`, mas
    // nada garante que não apareça um quarto valor, e fixá-los faria o mapa
    // esconder silenciosamente o que não conhecesse.
    const iEstadoViatura = new Map();
    const estadosViatura = [];
    const iMeio = new Map();
    const meios = [];
    const iCorrida = new Map();
    const corridas = [];
    const registar = (valor, indice, tabela) => {
        if (valor === '')
            return -1;
        const achado = indice.get(valor);
        if (achado !== undefined)
            return achado;
        const novo = tabela.length;
        tabela.push(valor);
        indice.set(valor, novo);
        return novo;
    };
    const grelha = csv.viaturas.map(() => new Array(nDias).fill(null));
    const movimentos = csv.viaturas.map(() => new Array(nDias).fill(null));
    for (const a of csv.alocacoes) {
        const vi = iViatura.get(a['viatura_id'] ?? '');
        if (vi === undefined)
            continue;
        const dia = diaDoAno(a['data'] ?? '', ano);
        if (dia < 0 || dia >= nDias)
            continue;
        const linha = grelha[vi];
        if (linha === undefined)
            continue;
        const existente = linha[dia];
        if (existente != null) {
            // Segunda alocação no mesmo dia e viatura = tripulação dupla (95 células
            // medidas na folha real, `David/Cotas` na folha original).
            //
            // A célula já existe e o resto dos seus campos é o mesmo — cliente,
            // itinerário e localidade vêm do itinerário, não do condutor. Só o
            // condutor é novo, e é isso que se acrescenta. Descartar esta linha
            // apagava metade da tripulação do mapa.
            const m2 = iMotorista.get(a['motorista_id'] ?? '') ?? -1;
            if (m2 >= 0 && m2 !== existente[2] && existente[6] < 0) {
                linha[dia] = [
                    existente[0],
                    existente[1],
                    existente[2],
                    existente[3],
                    existente[4] | BANDEIRA.TRIPULACAO_DUPLA,
                    existente[5],
                    m2,
                    existente[7],
                    existente[8],
                    existente[9],
                    existente[10],
                ];
            }
            continue;
        }
        const movIn = a['movimento_in'] ?? '';
        const movOut = a['movimento_out'] ?? '';
        let bandeiras = 0;
        if (movIn !== '')
            bandeiras |= BANDEIRA.MOVIMENTO_IN;
        if (movOut !== '')
            bandeiras |= BANDEIRA.MOVIMENTO_OUT;
        if (a['dia_rotacao'] === 'sim')
            bandeiras |= BANDEIRA.ROTACAO;
        if (a['estado_registo'] === 'reserva')
            bandeiras |= BANDEIRA.RESERVA;
        if (a['marcador_duvida'] === 'sim')
            bandeiras |= BANDEIRA.DUVIDA;
        const itin = a['itinerario'] ?? '';
        let ii = -1;
        if (itin !== '') {
            const achado = iItinerario.get(itin);
            if (achado === undefined) {
                ii = itinerarios.length;
                itinerarios.push(itin);
                iItinerario.set(itin, ii);
            }
            else {
                ii = achado;
            }
        }
        const papel = ESTADOS.indexOf((a['estado_visual'] ?? 'bom'));
        linha[dia] = [
            iCliente.get(a['cliente_id'] ?? '') ?? -1,
            iLocalidade.get(a['localidade'] ?? '') ?? -1,
            iMotorista.get(a['motorista_id'] ?? '') ?? -1,
            papel < 0 ? 0 : papel,
            bandeiras,
            ii,
            -1, // 2.º condutor: preenchido pela alocação seguinte, se houver
            registar(a['estado_viatura'] ?? '', iEstadoViatura, estadosViatura),
            registar(a['meio'] ?? '', iMeio, meios),
            iLocalidade.get(a['local_embarque'] ?? '') ?? -1,
            registar(a['referencia'] ?? '', iCorrida, corridas),
        ];
        const linhaMov = movimentos[vi];
        if (linhaMov !== undefined && (movIn !== '' || movOut !== '')) {
            linhaMov[dia] = [movIn, movOut];
        }
    }
    // Segunda passagem: fronteiras da corrida e VALIDAÇÃO da rotação.
    //
    // A fronteira agrupa por CORRIDA (a viagem) e não pela assinatura de rota: duas
    // viagens distintas e seguidas com a mesma origem/destino são duas barras, não
    // uma. Onde não há corrida identificada, cai-se na assinatura, que é o melhor
    // que existe.
    for (const linha of grelha) {
        for (let d = 0; d < nDias; d++) {
            const c = linha[d];
            if (c == null)
                continue;
            const anterior = d > 0 ? (linha[d - 1] ?? null) : null;
            const seguinte = d + 1 < nDias ? (linha[d + 1] ?? null) : null;
            const chave = (x) => (x[10] >= 0 ? x[10] : x[5]);
            const mesmaCorrida = (x) => x !== null && x[7] < 0 && c[7] < 0 && chave(x) === chave(c);
            let b = c[4];
            if (!mesmaCorrida(anterior))
                b |= BANDEIRA.PRIMEIRO_DIA;
            if (!mesmaCorrida(seguinte))
                b |= BANDEIRA.ULTIMO_DIA;
            // ---- a rotação é DERIVADA, não aceite ------------------------------
            //
            // Rotação = chega um itinerário E parte outro, no mesmo dia. Foi o cliente
            // que a definiu assim: «a hora de chegada de um itinerário e a partida de
            // outro, para o motorista se orientar».
            //
            // Logo exige duas coisas verificáveis:
            //   · uma CHEGADA — o dia anterior é da mesma corrida, ou seja há viagem a
            //     terminar aqui;
            //   · uma PARTIDA — o dia seguinte tem serviço, ou seja há viagem a começar.
            //
            // Um dia negro seguido de dia livre não é rotação nenhuma. Medido: 14,3 %
            // dos dias marcados estavam nessa situação.
            if ((b & BANDEIRA.ROTACAO) !== 0) {
                const temChegada = mesmaCorrida(anterior) || (b & BANDEIRA.MOVIMENTO_IN) !== 0;
                const temPartida = seguinte !== null && seguinte[7] < 0;
                if (!(temChegada && temPartida)) {
                    b &= ~BANDEIRA.ROTACAO;
                    b |= BANDEIRA.ROTACAO_SUSPEITA;
                }
            }
            linha[d] = [c[0], c[1], c[2], c[3], b, c[5], c[6], c[7], c[8], c[9], c[10]];
        }
    }
    return {
        ano,
        nDias,
        fimDeSemana,
        mesPorDia,
        diaDoMes,
        viaturas: csv.viaturas.map((v) => ({
            id: v['id'] ?? '',
            numero: v['numero_interno'] ?? '',
            matricula: v['matricula'] ?? '',
            lotacao: v['lotacao'] ?? '',
            // `A/C|WC|Wi-Fi` no CSV. Vazio dá lista vazia e não `['']`, que casaria com nada
            // e daria um requisito indeterminado sem razão.
            caracteristicas: (v['caracteristicas'] ?? '').split('|').filter((c) => c !== ''),
            motoristaPreferencial: iMotorista.get(v['motorista_preferencial'] ?? '') ?? -1,
            clienteDecoracao: iCliente.get(v['cliente_decoracao'] ?? '') ?? -1,
            plano: v['plano'] ?? '',
        })),
        clientes: csv.clientes.map((c) => [c['sigla'] ?? '', c['nome'] ?? '']),
        motoristas: csv.motoristas.map((m) => m['diminutivo'] ?? ''),
        localidades: csv.localidades.map((l) => [l['codigo_interno'] ?? '', l['nome'] ?? '']),
        // `-1` se os dados não declararem base. Não se adivinha pelo nome nem se fixa
        // o código: quem sabe qual é a base são os dados.
        localidadeBase: csv.localidades.findIndex((l) => l['e_base'] === 'sim'),
        itinerarios,
        estados: ESTADOS,
        estadosViatura,
        meios,
        corridas,
        grelha,
        movimentos,
    };
}
//# sourceMappingURL=mapa.js.map